import React, { useState, useCallback, useRef } from 'react';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
  RouteComponentProps,
} from 'react-router-dom';
import { Menu } from 'lego-dist';
import { useTranslation } from 'react-i18next';
import { stringify, parse } from 'query-string';

import { APP_SECTIONS_PATHS, menuItems } from '../constants/routes';
import CenterFilter from '../components/menu/CenterFilter';
import { menuRoutes, noMenuRoutes } from './routes';
import LocalAuth from '../views/LocalAuth';
import ObContainer from 'containers/ObContainer';
import TopBar from 'components/TopBar';
import { deleteProps } from 'lib/utils';
import { APP_VERSION } from 'config';
import styled from 'styled-components';
import SigninView from 'views/AuthView/SigninView';
import SignupView from 'views/AuthView/SignupView';
import ResetPasswordView from 'views/AuthView/reset-password';

const RouterContext = React.createContext<any>(null);

interface RoutesProps {}

interface IRouterContext extends RouteComponentProps {
  currentParamsRef?: any,
  appendParams?: any,
  addParamsToUrl?: any,
  overrideParamsToUrl?: any,
  navigateTo?: any,
  removeParamsToUrl?: any,
  isSameLocation?: any,
}

const LogInfo = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px 20px;
  color: ${({ theme }) => theme.colors.shadeMedium};
  font-size: 14px;
`;

const Routes: React.SFC<RoutesProps> = () => {
  const [t] = useTranslation();
  const [doingOb, setDoingOb] = useState(localStorage.getItem('doing_ob'));
  const [authToken] = useState(localStorage.getItem('auth_token'));
  const translatedMenuItems = menuItems.map((item) => ({
    ...item,
    title: t(item.title),
  }));

  const currentParamsRef = useRef({});
  const appendParams = useCallback(
    (currentParams: any, newParams: any = {}) => {
      const mergedParams = Object.assign(currentParams, newParams);
      currentParamsRef.current = mergedParams;
      return stringify(currentParamsRef.current);
    },
    [],
  );

  return (
    <Router basename="/app">
      <Route>
        {(routeProps) => {
          const { history, location } = routeProps;
          currentParamsRef.current = parse(location.search);
          /* Register global configurations */
          window.ConfigurationManager.registerConfig('router', routeProps);

          const handleScroll = (e: any) => {
            const { target: { scrollHeight, scrollTop, clientHeight } } = e;
            const urlParams = new URLSearchParams(window.location.search);
            const bottomPageUrlParam = urlParams.get('bottomPage');

            if (location.pathname === APP_SECTIONS_PATHS.STUDENTS_PROFILE
              && !bottomPageUrlParam
              && clientHeight > 100 // min height to listen scroll
            ) {
              const bottom = scrollHeight - scrollTop < clientHeight + 20;
              if (bottom) { // sets bottomPage url param to true
                urlParams.set('bottomPage', '1');
                const search = urlParams.toString();
                history.replace({ search });
              }
            }
          };

          const addParamsToUrl = (params: any = {}) => {
            history.push({
              search: appendParams(currentParamsRef.current, {
                ...params,
              }),
            });
          };

          const overrideParamsToUrl = (params: any = {}) => {
            currentParamsRef.current = params;
            const search = stringify(currentParamsRef.current);
            history.push({
              pathname: location.pathname,
              search,
            });
          };

          const removeParamsToUrl = (params: any[] = []) => {
            const currentParams = currentParamsRef.current;
            deleteProps(currentParams, params);
            const search = stringify(currentParams);
            history.push({
              pathname: location.pathname,
              search,
            });
          };

          const navigateTo = (pathname: string = '', params: any = {}) => {
            history.push({
              pathname,
              search: appendParams(currentParamsRef.current, {
                ...params,
              }),
            });
          };

          const isSameLocation = (pathname: string) => history.location.pathname === pathname;

          return (
            <RouterContext.Provider
              value={{
                ...routeProps,
                currentParamsRef,
                appendParams,
                addParamsToUrl,
                overrideParamsToUrl,
                removeParamsToUrl,
                navigateTo,
                isSameLocation,
              }}
            >
              <Switch>
                {doingOb && (
                  <>
                    <Redirect to={APP_SECTIONS_PATHS.ON_BOARDING} />
                    <Route
                      path={APP_SECTIONS_PATHS.ON_BOARDING}
                      render={(props) => (
                        <ObContainer
                          history={props.history}
                          setDoingOb={setDoingOb}
                        />
                      )}
                    />
                  </>
                )}
                {noMenuRoutes.map((route: any) => {
                  const Component = route.component;
                  return (
                    <Route
                      key={route.path}
                      exact={route.exact}
                      path={route.path}
                      render={(renderProps) => <Component {...renderProps} />}
                    />
                  );
                })}
                {authToken && (
                  <Menu
                    onScroll={handleScroll}
                    topBarContent={({ toggleShow }: any) => (
                      <TopBar history={history} toggleMenu={toggleShow} />
                    )}
                    items={translatedMenuItems}
                    activeRoute={location.pathname}
                    onItemClicked={({ route }: any) => history.push(route)}
                    onClickMenuNavListItem={({ route, clickAction }: any) => {
                      history.push(route);
                      if (clickAction) clickAction();
                    }}
                    content={() => (
                      <Switch>
                        {menuRoutes.map(
                          ({ path, exact, component: Component }: any) => (
                            <Route
                              key={path}
                              path={path}
                              exact={exact}
                              render={(renderProps) => (
                                <Component {...renderProps} />
                              )}
                            />
                          ),
                        )}
                      </Switch>
                    )}
                    afterListRender={() => (
                      <>
                        <CenterFilter />
                        <LogInfo>
                          {`v${APP_VERSION}`}
                        </LogInfo>
                      </>
                    )}
                  />
                )}
                {!authToken && (
                  <Route>
                    <Route
                      path={APP_SECTIONS_PATHS.LOCAL_AUTH}
                      component={LocalAuth}
                    />
                    <Route
                      path={APP_SECTIONS_PATHS.SIGNIN}
                      component={SigninView}
                    />
                    <Route
                      path={APP_SECTIONS_PATHS.SIGNIN_WITH_LANG}
                      component={SigninView}
                    />
                    <Route
                      path={APP_SECTIONS_PATHS.SIGNUP}
                      component={SignupView}
                    />
                    <Route
                      path={APP_SECTIONS_PATHS.SIGNUP_WITH_LANG}
                      component={SignupView}
                    />
                    <Route
                      path={APP_SECTIONS_PATHS.RESET_PASSWORD}
                      component={ResetPasswordView}
                    />
                    <Route
                      path={APP_SECTIONS_PATHS.RESET_PASSWORD_WITH_LANG}
                      component={ResetPasswordView}
                    />
                    <Redirect to={APP_SECTIONS_PATHS.SIGNIN} />
                  </Route>
                )}
              </Switch>
            </RouterContext.Provider>
          );
        }}
      </Route>
    </Router>
  );
};


export function useRouter() {
  const routerContext: IRouterContext = React.useContext(RouterContext);
  return routerContext;
}

export default Routes;
